import React, { useEffect, useMemo, useState } from 'react';
import { ReservedBadge } from "../../components/TestAccountPanel";
import { useNavigate } from 'react-router-dom';
import './adminDashboard.css';
import BASE_URL from "../../config";

const PAGE_SIZE = 50;
const COLUMNS = [
    { key: 'id', label: 'ID', cls: 'ad-col-id' },
    { key: 'username', label: 'Username' },
    { key: 'name', label: 'Name' },
    { key: 'glasses', label: 'Glasses' },
    { key: 'case', label: 'Case' },
    { key: 'date_joined', label: 'Joined', noSearch: true, cls: 'ad-h-joined' },
    { key: 'contact_number', label: 'Contact', cls: 'ad-h-contact' },
    { key: 'email', label: 'Email', cls: 'ad-h-email' },
    { key: 'location', label: 'City / Postcode', cls: 'ad-h-loc' },
];
const EMPTY = { id: '', username: '', name: '', glasses: '', case: '', email: '', contact_number: '', location: '' };
const RESERVED = /^(test|demo)\d{2}@mavistech\.uk$/i;

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [devices, setDevices] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState(EMPTY);
    const [activeSearchKey, setActiveSearchKey] = useState(null);
    const [chip, setChip] = useState('all');
    const [sort, setSort] = useState({ key: 'id', dir: 'desc' });
    const [page, setPage] = useState(0);

    useEffect(() => {
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('jwt_token')}` };
        fetch(`${BASE_URL}/api/getusers`, { headers })
            .then((r) => { if (!r.ok) throw new Error('Failed to fetch'); return r.json(); })
            .then((data) => { setCustomers(data); setLoading(false); })
            .catch(() => { setError('Error fetching data'); setLoading(false); });
        // Serial numbers are loaded separately so the list never waits on them.
        fetch(`${BASE_URL}/api/admin/users-devices/`, { headers })
            .then((r) => (r.ok ? r.json() : {}))
            .then(setDevices)
            .catch(() => setDevices({}));
    }, []);

    // One flat, lower-cased search row per user (built once, not on every keystroke).
    const rows = useMemo(() => customers.map((c) => {
        const d = devices[c.id] || { glasses: [], cases: [] };
        const r = {
            ...c,
            name: [c.first_name, c.last_name].filter(Boolean).join(' '),
            glasses: d.glasses.join(', '),
            case: d.cases.join(', '),
            location: [c.city, c.postcode].filter(Boolean).join(' '),
            has_device: d.glasses.length > 0,
            reserved: RESERVED.test(c.username || ''),
        };
        r._all = [c.id, c.username, r.name, r.glasses, r.case, c.email, c.contact_number, r.location, c.address_line_1]
            .filter((x) => x !== null && x !== undefined).join(' ').toLowerCase();
        return r;
    }), [customers, devices]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const out = rows.filter((r) => {
            if (q && !r._all.includes(q)) return false;
            if (chip === 'active' && !r.is_active) return false;
            if (chip === 'inactive' && r.is_active) return false;
            if (chip === 'device' && !r.has_device) return false;
            if (chip === 'nodevice' && r.has_device) return false;
            if (chip === 'reserved' && !r.reserved) return false;
            return Object.entries(filters).every(([k, v]) => !v || String(r[k] ?? '').toLowerCase().includes(v.toLowerCase()));
        });
        const { key, dir } = sort;
        const m = dir === 'asc' ? 1 : -1;
        out.sort((a, b) => {
            const x = a[key]; const y = b[key];
            if (key === 'id') return (x - y) * m;
            return String(x ?? '').localeCompare(String(y ?? ''), undefined, { numeric: true }) * m;
        });
        return out;
    }, [rows, query, chip, filters, sort]);

    useEffect(() => { setPage(0); }, [query, chip, filters, sort]);

    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const view = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    const anyFilter = query || Object.values(filters).some(Boolean) || chip !== 'all';
    const clearAll = () => { setQuery(''); setFilters(EMPTY); setChip('all'); setActiveSearchKey(null); };
    const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

    if (loading) {
        return (<div className="aws-dashboard"><div className="aws-loading"><div className="aws-spinner"></div><p>Loading user data...</p></div></div>);
    }
    if (error) {
        return (<div className="aws-dashboard"><div className="aws-error"><div className="error-icon">⚠️</div><h3>Error</h3><p>{error}</p></div></div>);
    }

    const chips = [
        ['all', 'All'], ['active', 'Active'], ['inactive', 'Inactive'],
        ['device', 'With glasses'], ['nodevice', 'No glasses'], ['reserved', 'Test / Demo'],
    ];

    return (
        <div className="aws-dashboard ad-compact">
            <div className="aws-header">
                <div className="header-content">
                    <h1 className="aws-title">User Management</h1>
                    <p className="aws-subtitle">Search by name, email, glasses serial or case serial</p>
                </div>
                <div className="header-stats">
                    <div className="stat-card"><div className="stat-label">Total Users</div><div className="stat-value">{customers.length}</div></div>
                    <div className="stat-card"><div className="stat-label">Active</div><div className="stat-value active">{customers.filter((c) => c.is_active).length}</div></div>
                    <div className="stat-card"><div className="stat-label">Inactive</div><div className="stat-value inactive">{customers.filter((c) => !c.is_active).length}</div></div>
                </div>
            </div>

            <div className="aws-content">
                <div className="table-container">
                    <div className="ad-toolbar">
                        <input
                            type="search"
                            className="ad-search"
                            aria-label="Search users, glasses serial or case serial"
                            placeholder="Search anything: name, email, glasses serial (SG287), case serial…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoFocus
                        />
                        <div className="ad-chips" role="group" aria-label="Quick filters">
                            {chips.map(([k, label]) => (
                                <button key={k} type="button" className={`ad-chip ${chip === k ? 'on' : ''}`} aria-pressed={chip === k} onClick={() => setChip(k)}>{label}</button>
                            ))}
                        </div>
                        <span className="ad-count">{filtered.length} user{filtered.length === 1 ? '' : 's'}</span>
                        {anyFilter && <button type="button" className="clear-all-filters" onClick={clearAll}>Clear all</button>}
                    </div>

                    <div className="table-wrapper ad-wrapper">
                        <table className="aws-table">
                            <thead>
                                <tr>
                                    {COLUMNS.map(({ key, label, noSearch, cls }) => (
                                        <th key={key} className={cls}>
                                            <div className="th-content">
                                                <button type="button" className="ad-sort" onClick={() => toggleSort(key === 'location' ? 'city' : key)} title="Sort">
                                                    <span className="th-label">{label}</span>
                                                    {sort.key === (key === 'location' ? 'city' : key) && <span aria-hidden="true">{sort.dir === 'asc' ? ' ▲' : ' ▼'}</span>}
                                                </button>
                                                {!noSearch && (
                                                    <button
                                                        type="button"
                                                        className={`search-toggle ${activeSearchKey === key || filters[key] ? 'active' : ''}`}
                                                        onClick={() => setActiveSearchKey((k) => (k === key ? null : key))}
                                                        title={`Filter ${label}`}
                                                        aria-label={`Filter by ${label}`}
                                                    >
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>
                                                    </button>
                                                )}
                                            </div>
                                            {activeSearchKey === key && (
                                                <div className="search-input-container">
                                                    <input type="text" className="search-input" placeholder={`Filter ${label.toLowerCase()}…`}
                                                        value={filters[key]} onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))} autoFocus />
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {view.length === 0 ? (
                                    <tr><td colSpan={COLUMNS.length} className="no-results"><div className="no-results-content"><p>No users found</p></div></td></tr>
                                ) : view.map((c) => (
                                    <tr key={c.id} className={`clickable-row ${c.is_active ? "" : "ad-row-inactive"}`} onClick={() => navigate(`/customer/${c.id}`)}>
                                        <td className="ad-col-id">{c.id}</td>
                                        <td className="username-cell ad-user">{c.username}<ReservedBadge username={c.username} /></td>
                                        <td>{c.name || '-'}</td>
                                        <td className="ad-serial">{c.glasses || '-'}</td>
                                        <td className="ad-serial">{c.case || '-'}</td>
                                        <td className="date-cell ad-h-joined">
                                            {c.date_joined ? new Date(c.date_joined).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                                        </td>
                                        <td className="ad-h-contact">{c.contact_number || '-'}</td>
                                        <td className="email-cell ad-h-email">{c.email}</td>
                                        <td className="ad-h-loc">{c.location || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pages > 1 && (
                        <div className="ad-pager">
                            <button type="button" disabled={page === 0} onClick={() => setPage(0)}>« First</button>
                            <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
                            <span>Page {page + 1} of {pages}</span>
                            <button type="button" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>Next ›</button>
                            <button type="button" disabled={page >= pages - 1} onClick={() => setPage(pages - 1)}>Last »</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
